import React, { Fragment, useContext, useMemo, useState, useEffect, useRef } from 'react'
import { AppContext } from '../appContext'
import { selectChecklistItemsByPhase } from '../selectors/checklistItems'
import ChecklistItemFundRequest from './checklist-item-fund-request'
import ChecklistItemSolarMonitoring from './checklist-item-solar-monitoring'
import ChecklistDocItemUpload from './checklist-doc-item-upload'
import { FaAngleRight, FaAngleDown } from 'react-icons/fa6'
import {
  ChecklistPhase,
  FundReleaseType,
  Adders,
  IChecklistItem,
  ChecklistItemType,
  StatusData,
  LoanFundingPhaseShared,
  FundingSchedule,
  isInspectionRequestChecklistItem,
  isSolarMonitoringChecklistItem,
} from '@oneethos/shared'
import ChecklistItemInspectionRequest from './checklist-item-inspection-request'

type LoanFundingPhaseProps = {
  project: StatusData
  onProjectChange?: (p: StatusData) => void
  disabled?: boolean
}

type GroupedItems = {
  fundReleaseType: FundReleaseType
  checklistItems: IChecklistItem[]
}

const getFundReleaseDisplayName = (type: FundReleaseType): string => {
  return type.replace("Funding ", "")
}

const groupChecklistItemsByFundRelease = (
  project: StatusData,
  checklistItems: IChecklistItem[],
  showSolarMonitoring: boolean,
): GroupedItems[] => {
  const activeTypes = Object.keys(project?.fundReleaseAmounts ?? {})
    .filter(key => (project?.fundReleaseAmounts?.[key as FundReleaseType] ?? 0) > 0) as FundReleaseType[]

  const hasFullFunding = activeTypes.includes(FundReleaseType.FullFundingRelease)
  const roofPrice = new Adders(project.adders).getRoofPrice()

  const filteredItems = checklistItems.filter(item => {
    if (hasFullFunding && roofPrice === 0) {
      return !item.fundReleaseTypes?.includes(FundReleaseType.RoofFundingRelease)
    }
    return true
  })

  return activeTypes.map(type => ({
    fundReleaseType: type,
    checklistItems: filteredItems.filter(item => {
      if (
        item.fundingSchedules?.length &&
        !item.fundingSchedules.includes(project.fundingSchedule)
      ) {
        return false
      }

      if (isSolarMonitoringChecklistItem(item)) {
        if (!showSolarMonitoring || !project.monitoringSiteId) return false
        if (project.fundingSchedule !== FundingSchedule.SeventyThirty) {
          return false
        }
        return Boolean(item.fundReleaseTypes?.includes(type))
      }
      return item.fundReleaseTypes?.includes(type)
    }),
  }))
}

export const LoanFundingPhase = ({
  project,
  onProjectChange,
  disabled = false,
}: LoanFundingPhaseProps) => {
  const { state } = useContext(AppContext)
  const showSolarMonitoring = Boolean(state.registration?.installer?.isSupport)
  const fundingPhaseItems = selectChecklistItemsByPhase(state, ChecklistPhase.LoanFunding)
  const [expandedRows, setExpandedRows] = useState<string[]>([])
  const [expandedTypes, setExpandedTypes] = useState<Record<string, boolean> | null>(null)
  const previousActiveType = useRef<string | undefined>(undefined)
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (!hasInitialized.current) {
      const allItemIds = fundingPhaseItems.map(item => item.id)

      setExpandedRows(allItemIds)
      hasInitialized.current = true
    }
  }, [fundingPhaseItems, project])

  const toggleRow = (id: string) => {
    setExpandedRows(prev =>
      prev.includes(id)
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id])
  }

  const groupedChecklistItems = useMemo(
    () => groupChecklistItemsByFundRelease(project, fundingPhaseItems, showSolarMonitoring),
    [project, fundingPhaseItems, showSolarMonitoring],
  )

  const loanFundingReleaseGroups = LoanFundingPhaseShared.getLoanFundingReleaseGroups(groupedChecklistItems, project)
  const activeFundReleaseType = loanFundingReleaseGroups.find(entry => entry.active)?.group.fundReleaseType

  useEffect(() => {
    if (previousActiveType.current === activeFundReleaseType) return
    previousActiveType.current = activeFundReleaseType
    setExpandedTypes(null)
  }, [activeFundReleaseType])

  const isGroupExpanded = (fundReleaseType: string, defaultExpanded: boolean): boolean => {
    if (!expandedTypes) return defaultExpanded
    return Boolean(expandedTypes[fundReleaseType])
  }

  const toggleFundReleaseType = (fundReleaseType: string) => {
    setExpandedTypes(current => {
      const base = current ?? Object.fromEntries(
        loanFundingReleaseGroups.map(({ group, active }) => [
          group.fundReleaseType,
          Boolean(active) && !disabled,
        ]),
      )
      return {
        ...base,
        [fundReleaseType]: !base[fundReleaseType],
      }
    })
  }

  return (
    <div className="table-responsive">
      <table className="table">
        {loanFundingReleaseGroups.map(({ group: { fundReleaseType, checklistItems }, active }) => {
          // Only the active release accepts input. Other releases stay expandable so
          // their checklist items can be read after the project moves on.
          const isCurrentStage = Boolean(active) && !disabled
          const shouldShowItems = isGroupExpanded(fundReleaseType, isCurrentStage)

          return (
            <Fragment key={fundReleaseType}>
              <tbody>
                <tr
                  className={`text-center pointer ${isCurrentStage ? '' : 'gray-out'}`}
                  onClick={() => toggleFundReleaseType(fundReleaseType)}
                >
                  <td colSpan={5} className="fw-bold p-2">
                    {getFundReleaseDisplayName(fundReleaseType)}
                    <span className="ms-2">
                      {shouldShowItems ? <FaAngleDown /> : <FaAngleRight />}
                    </span>
                  </td>
                </tr>
              </tbody>
              {shouldShowItems && (
                <tbody
                  className={isCurrentStage ? '' : 'gray-out'}
                  {...(isCurrentStage ? {} : { inert: '' })}
                >
                  {checklistItems.map(item => {
                    const isExpanded = expandedRows.includes(item.id) && isCurrentStage

                    return (
                      <Fragment key={item.id}>
                        {(item.type === ChecklistItemType.FundingRequest || item.type === ChecklistItemType.Airtable) &&
                          <ChecklistItemFundRequest
                            item={item}
                            project={project}
                            onClick={() => toggleRow(item.id)}
                            isExpanded={isExpanded}
                            onProjectChange={onProjectChange}
                          />
                        }
                        {isInspectionRequestChecklistItem(item) &&
                          <ChecklistItemInspectionRequest
                            item={item}
                            project={project}
                            onClick={() => toggleRow(item.id)}
                            isExpanded={isExpanded}
                            onProjectChange={onProjectChange}
                          />
                        }
                        {item.type === ChecklistItemType.DocumentUpload &&
                          <ChecklistDocItemUpload
                            item={item}
                            project={project}
                            handleRowToggle={() => toggleRow(item.id)}
                            isExpanded={isExpanded}
                            onProjectChange={onProjectChange}
                          />
                        }
                        {isSolarMonitoringChecklistItem(item) &&
                          <ChecklistItemSolarMonitoring
                            item={item}
                            project={project}
                            onClick={() => toggleRow(item.id)}
                            isExpanded={isExpanded}
                            onProjectChange={onProjectChange}
                          />}
                      </Fragment>
                    )
                  })}
                </tbody>
              )}
            </Fragment>
          )
        })}
      </table>
    </div>
  )
}
